import { scrollToElement } from '../../utils/scrollToElement';

/** First visible element matching selector list (desktop vs mobile anchors). */
export function resolveTourTarget(selectors: string | string[]): HTMLElement | null {
    const all = resolveTourTargets(selectors);
    return all[0] ?? null;
}

/**
 * All distinct visible elements for the selector list (order preserved).
 * Used when a step rings multiple controls (e.g. Find + recommendations).
 */
export function resolveTourTargets(selectors: string | string[]): HTMLElement[] {
    const list = Array.isArray(selectors) ? selectors : [selectors];
    const found: HTMLElement[] = [];
    const seen = new Set<HTMLElement>();
    for (const sel of list) {
        try {
            const nodes = document.querySelectorAll(sel);
            for (const node of nodes) {
                if (!(node instanceof HTMLElement)) continue;
                if (!isElementVisible(node)) continue;
                if (seen.has(node)) continue;
                seen.add(node);
                found.push(node);
                break; // one match per selector entry
            }
        } catch {
            /* invalid selector */
        }
    }
    return found;
}

export function isElementVisible(el: HTMLElement): boolean {
    if (!el.isConnected) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
        return false;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width < 2 && rect.height < 2) return false;
    return true;
}

/** Visible on-screen box (layout coords). Under pinch-zoom this is smaller than the layout viewport. */
export type VisualViewportBox = {
    offsetTop: number;
    offsetLeft: number;
    width: number;
    height: number;
    scale: number;
};

export function readVisualViewportBox(): VisualViewportBox {
    const vv = window.visualViewport;
    if (!vv) {
        return {
            offsetTop: 0,
            offsetLeft: 0,
            width: window.innerWidth || 390,
            height: window.innerHeight || 844,
            scale: 1,
        };
    }
    return {
        offsetTop: vv.offsetTop,
        offsetLeft: vv.offsetLeft,
        width: vv.width,
        height: vv.height,
        scale: vv.scale,
    };
}

function freeChromeInsets(): { top: number; bottom: number } {
    const top = readCssPx('--shop-header-height', 112);
    let bottom = 0;
    if (window.matchMedia('(max-width: 1023px)').matches) {
        const nav = getMobileBottomNavEl();
        const vv = readVisualViewportBox();
        if (nav) {
            // Distance from bottom of *visible* viewport to nav top (layout coords)
            bottom = Math.max(0, vv.offsetTop + vv.height - nav.getBoundingClientRect().top);
        } else {
            bottom = readCssPx('--mobile-bottom-nav-height', 64);
        }
    }
    return { top, bottom };
}

function readCssPx(varName: string, fallback: number): number {
    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;visibility:hidden;height:var(${varName}, ${fallback}px)`;
    document.body.appendChild(probe);
    const h = probe.getBoundingClientRect().height || fallback;
    probe.remove();
    return h;
}

/**
 * Force the target into the free band between sticky header and bottom nav.
 * Does NOT use block:nearest (often no-ops). Instant — tours remeasure after.
 *
 * Fixed/sticky chrome (bottom-nav Sell, My Journey edge tab) must NOT trigger
 * document scroll: their getBoundingClientRect is viewport-fixed, so "centering"
 * them only shifts the page underneath (looked like scrolling down by a product).
 */
export function scrollTargetIntoView(el: HTMLElement): void {
    const style = window.getComputedStyle(el);
    if (
        style.position === 'fixed' ||
        style.position === 'sticky' ||
        el.closest('[data-mobile-bottom-nav]')
    ) {
        return;
    }

    const { top: offsetTop, bottom: offsetBottom } = freeChromeInsets();
    const vv = readVisualViewportBox();
    const rect = el.getBoundingClientRect();
    // Free band inside the *visible* viewport (pinch-zoom aware)
    const freeTop = Math.max(vv.offsetTop + 10, offsetTop + 10);
    const freeBottom = vv.offsetTop + vv.height - offsetBottom - 10;
    const freeH = Math.max(80, freeBottom - freeTop);

    // Tall targets (checklist): pin top under header
    const block = rect.height > freeH * 0.85 ? 'start' : 'center';

    // Always scroll if any edge is outside free band (not just "nearest")
    const needsScroll = rect.top < freeTop || rect.bottom > freeBottom || rect.top > freeBottom - 40;
    if (!needsScroll && block === 'center') {
        // Still fine-tune if mostly visible but not well centered for short targets
        const mid = (rect.top + rect.bottom) / 2;
        const freeMid = (freeTop + freeBottom) / 2;
        if (Math.abs(mid - freeMid) < 48) return;
    }

    scrollToElement(el, {
        behavior: 'auto',
        block: block === 'start' ? 'start' : 'center',
        offsetTop,
        offsetBottom,
    });
}

export type Rect = { top: number; left: number; width: number; height: number; bottom: number; right: number };

/** Spotlight shape including per-corner radii so the ring matches the control (e.g. rounded-l-xl). */
export type SpotlightShape = Rect & {
    /** CSS border-radius shorthand: "tl tr br bl" in px */
    borderRadius: string;
    /** Corner radii in CSS px (for SVG path) */
    radii: { tl: number; tr: number; br: number; bl: number };
};

function parseRadiusPx(value: string): number {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
}

function readCornerRadii(el: HTMLElement): { tl: number; tr: number; br: number; bl: number } {
    const s = window.getComputedStyle(el);
    return {
        tl: parseRadiusPx(s.borderTopLeftRadius),
        tr: parseRadiusPx(s.borderTopRightRadius),
        br: parseRadiusPx(s.borderBottomRightRadius),
        bl: parseRadiusPx(s.borderBottomLeftRadius),
    };
}

/** Expand corner radii slightly when padding the spotlight so the outline stays proportional. */
function expandRadii(
    r: { tl: number; tr: number; br: number; bl: number },
    pad: number
): { tl: number; tr: number; br: number; bl: number } {
    const grow = (v: number) => (v > 0 ? v + pad : 0);
    return { tl: grow(r.tl), tr: grow(r.tr), br: grow(r.br), bl: grow(r.bl) };
}

/**
 * SVG path for a rect with independent corner radii (matches Tailwind rounded-l-xl etc.).
 */
export function roundedRectPath(
    x: number,
    y: number,
    w: number,
    h: number,
    radii: { tl: number; tr: number; br: number; bl: number }
): string {
    const maxR = Math.min(w, h) / 2;
    const tl = Math.min(radii.tl, maxR);
    const tr = Math.min(radii.tr, maxR);
    const br = Math.min(radii.br, maxR);
    const bl = Math.min(radii.bl, maxR);

    // Clockwise from top-left
    return [
        `M ${x + tl} ${y}`,
        `L ${x + w - tr} ${y}`,
        tr > 0 ? `A ${tr} ${tr} 0 0 1 ${x + w} ${y + tr}` : `L ${x + w} ${y}`,
        `L ${x + w} ${y + h - br}`,
        br > 0 ? `A ${br} ${br} 0 0 1 ${x + w - br} ${y + h}` : `L ${x + w} ${y + h}`,
        `L ${x + bl} ${y + h}`,
        bl > 0 ? `A ${bl} ${bl} 0 0 1 ${x} ${y + h - bl}` : `L ${x} ${y + h}`,
        `L ${x} ${y + tl}`,
        tl > 0 ? `A ${tl} ${tl} 0 0 1 ${x + tl} ${y}` : `L ${x} ${y}`,
        'Z',
    ].join(' ');
}

export function getTargetRect(el: HTMLElement, padding = 6): Rect {
    const r = el.getBoundingClientRect();
    return {
        top: r.top - padding,
        left: r.left - padding,
        width: r.width + padding * 2,
        height: r.height + padding * 2,
        bottom: r.bottom + padding,
        right: r.right + padding,
    };
}

/**
 * Spotlight shape for a target — geometry + real border-radius of the control.
 * - fixed/sticky chrome (My Journey edge tab, bottom-nav Sell): full element box
 * - document content: clip to free band under sticky header / above bottom nav
 */
export function getSpotlightRect(el: HTMLElement, padding = 6): SpotlightShape {
    const raw = getTargetRect(el, padding);
    const style = window.getComputedStyle(el);
    const isChrome =
        style.position === 'fixed' ||
        style.position === 'sticky' ||
        !!el.closest('[data-mobile-bottom-nav]');

    const radii = expandRadii(readCornerRadii(el), padding);

    const toShape = (rect: Rect): SpotlightShape => {
        const maxR = Math.min(rect.width, rect.height) / 2;
        const clamped = {
            tl: Math.min(radii.tl, maxR),
            tr: Math.min(radii.tr, maxR),
            br: Math.min(radii.br, maxR),
            bl: Math.min(radii.bl, maxR),
        };
        return {
            ...rect,
            radii: clamped,
            borderRadius: `${clamped.tl}px ${clamped.tr}px ${clamped.br}px ${clamped.bl}px`,
        };
    };

    const vv = readVisualViewportBox();
    const visLeft = vv.offsetLeft + 2;
    const visTop = vv.offsetTop + 2;
    const visRight = vv.offsetLeft + vv.width - 2;
    const visBottom = vv.offsetTop + vv.height - 2;

    // Header / sticky chrome / bottom-nav: never free-band clip (would shove rects below the header)
    if (
        isChrome ||
        !!el.closest('header') ||
        !!el.closest('[data-tour-id="home.notifications"]') ||
        !!el.closest('[data-tour-id="tour.help"]')
    ) {
        const top = Math.max(visTop, raw.top);
        const left = Math.max(visLeft, raw.left);
        const right = Math.min(visRight, raw.right);
        const bottom = Math.min(visBottom, raw.bottom);
        const width = Math.max(0, right - left);
        const height = Math.max(0, bottom - top);
        if (height < 8 || width < 8) return toShape(raw);
        return toShape({ top, left, width, height, bottom, right });
    }

    const { top: chromeTop, bottom: chromeBottom } = freeChromeInsets();
    const freeTop = Math.max(visTop, chromeTop + 4);
    const freeBottom = Math.min(visBottom, vv.offsetTop + vv.height - chromeBottom - 4);

    const top = Math.max(raw.top, freeTop);
    const bottom = Math.min(raw.bottom, freeBottom);
    const left = Math.max(visLeft, raw.left);
    const right = Math.min(visRight, raw.right);
    const height = Math.max(0, bottom - top);
    const width = Math.max(0, right - left);

    if (height < 8 || width < 8) return toShape(raw);

    return toShape({ top, left, width, height, bottom: top + height, right: left + width });
}

/** Sticky mobile bottom nav (hidden on lg+). */
export function getMobileBottomNavEl(): HTMLElement | null {
    const el = document.querySelector('[data-mobile-bottom-nav]');
    if (!(el instanceof HTMLElement)) return null;
    if (!isElementVisible(el)) return null;
    return el;
}

export type Placement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

/**
 * Place tooltip so its bottom edge is `gapPx` above the sticky bottom nav top.
 */
export function placeAboveBottomNav(
    tooltipW: number,
    tooltipH: number,
    gapPx = 8,
    alignTo?: Rect | null
): { top: number; left: number; placement: 'top' } | null {
    const nav = getMobileBottomNavEl();
    if (!nav) return null;

    const navTop = nav.getBoundingClientRect().top;
    const vv = readVisualViewportBox();
    const edge = 8;
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

    let top = navTop - gapPx - tooltipH;
    top = clamp(top, vv.offsetTop + edge, Math.max(vv.offsetTop + edge, vv.offsetTop + vv.height - tooltipH - edge));

    let left: number;
    if (alignTo) {
        left = alignTo.left + alignTo.width / 2 - tooltipW / 2;
    } else {
        left = vv.offsetLeft + (vv.width - tooltipW) / 2;
    }
    left = clamp(
        left,
        vv.offsetLeft + edge,
        Math.max(vv.offsetLeft + edge, vv.offsetLeft + vv.width - tooltipW - edge)
    );

    return { top, left, placement: 'top' };
}

export function placeTooltip(
    target: Rect,
    tooltipW: number,
    tooltipH: number,
    preferred: Placement = 'auto'
): { top: number; left: number; placement: Exclude<Placement, 'auto'> } {
    const gap = 12;
    // Clamp into the *visible* viewport so pinch-zoom + pan keeps the card on-screen
    const vv = readVisualViewportBox();
    const { bottom: chromeBottom } = freeChromeInsets();
    const edge = 8;
    // IMPORTANT: do NOT use full sticky-header height as minTop.
    // Header controls (bell / ?) sit inside the header — prod places the card
    // just under the icon (~target.bottom + gap). Using chromeTop (~150–170px)
    // forced steps 3–4 too low on mobile.
    const minTop = vv.offsetTop + edge;
    const maxTop = Math.max(minTop, vv.offsetTop + vv.height - chromeBottom - tooltipH - edge);
    const minLeft = vv.offsetLeft + edge;
    const maxLeft = Math.max(vv.offsetLeft + edge, vv.offsetLeft + vv.width - tooltipW - edge);
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

    const nearBottom = target.bottom > vv.offsetTop + vv.height - chromeBottom - 80;
    const nearTop = target.top < vv.offsetTop + 120;

    const candidates: Array<{
        placement: Exclude<Placement, 'auto'>;
        top: number;
        left: number;
        score: number;
    }> = [
        {
            placement: 'bottom',
            top: target.bottom + gap,
            left: target.left + target.width / 2 - tooltipW / 2,
            score: preferred === 'bottom' ? 100 : preferred === 'auto' ? 40 : 10,
        },
        {
            placement: 'top',
            top: target.top - gap - tooltipH,
            left: target.left + target.width / 2 - tooltipW / 2,
            score: preferred === 'top' ? 100 : preferred === 'auto' ? 35 : 10,
        },
        {
            placement: 'left',
            top: target.top + target.height / 2 - tooltipH / 2,
            left: target.left - gap - tooltipW,
            score: preferred === 'left' ? 100 : preferred === 'auto' ? 30 : 10,
        },
        {
            placement: 'right',
            top: target.top + target.height / 2 - tooltipH / 2,
            left: target.right + gap,
            score: preferred === 'right' ? 100 : preferred === 'auto' ? 25 : 10,
        },
    ];

    if (nearBottom && preferred !== 'bottom') {
        for (const c of candidates) {
            if (c.placement === 'top') c.score += 50;
            if (c.placement === 'bottom') c.score -= 60;
        }
    }
    // Header icons: strongly prefer sitting just under the control (prod behaviour)
    if (nearTop && preferred !== 'top') {
        for (const c of candidates) {
            if (c.placement === 'bottom') c.score += 55;
            if (c.placement === 'top') c.score -= 40;
            // Left/right of a top-right header control often overflows or sits mid-screen after clamp
            if (c.placement === 'left' || c.placement === 'right') c.score -= 25;
        }
    }

    const visBottom = vv.offsetTop + vv.height;
    const visRight = vv.offsetLeft + vv.width;

    for (const c of candidates) {
        if (c.top < minTop) c.score -= 50;
        if (c.left < minLeft) c.score -= 50;
        if (c.top + tooltipH > visBottom - chromeBottom - edge) c.score -= 50;
        if (c.left + tooltipW > visRight - edge) c.score -= 50;

        const coversTarget =
            c.left < target.right &&
            c.left + tooltipW > target.left &&
            c.top < target.bottom &&
            c.top + tooltipH > target.top;
        if (coversTarget) c.score -= 100;
    }

    candidates.sort((a, b) => b.score - a.score);
    const best = candidates[0];
    let top = clamp(best.top, minTop, maxTop);
    let left = clamp(best.left, minLeft, maxLeft);
    let placement = best.placement;

    // Hard avoid covering the spotlight target
    const covers =
        left < target.right &&
        left + tooltipW > target.left &&
        top < target.bottom &&
        top + tooltipH > target.top;
    if (covers) {
        const above = target.top - gap - tooltipH;
        const below = target.bottom + gap;
        if (below + tooltipH <= visBottom - chromeBottom - edge || above < minTop) {
            top = below;
            placement = 'bottom';
        } else if (above >= minTop) {
            top = above;
            placement = 'top';
        } else {
            top = minTop;
            placement = 'bottom';
        }
        left = clamp(target.left + target.width / 2 - tooltipW / 2, minLeft, maxLeft);
    }

    return { placement, top: clamp(top, minTop, maxTop), left };
}

/** Cookie consent still undecided — do not auto-stack tours. */
export function isCookieConsentPending(): boolean {
    try {
        return localStorage.getItem('Petito_cookie_consent') == null;
    } catch {
        return false;
    }
}

/** Pinch-zoom (or browser page zoom) beyond layout 100%. */
export function isVisualViewportZoomed(threshold = 0.02): boolean {
    const vv = window.visualViewport;
    if (!vv) return false;
    return Math.abs(vv.scale - 1) > threshold;
}

const DEFAULT_VIEWPORT_CONTENT = 'width=device-width, initial-scale=1.0, viewport-fit=cover';
const TOUR_VIEWPORT_CONTENT =
    'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover';

let savedViewportContent: string | null = null;
let tourViewportLocked = false;

/**
 * Best-effort unzoom for the product tour.
 * - Temporarily sets maximum-scale=1 (some browsers drop pinch-zoom; iOS often ignores).
 * - Does NOT scroll the document to the top.
 * Restore with restoreViewportAfterTour when the tour ends.
 */
export async function normalizeViewportForTour(): Promise<{ stillZoomed: boolean }> {
    try {
        const meta = document.querySelector('meta[name="viewport"]');
        if (meta instanceof HTMLMetaElement && !tourViewportLocked) {
            savedViewportContent = meta.getAttribute('content');
            meta.setAttribute('content', TOUR_VIEWPORT_CONTENT);
            tourViewportLocked = true;
        }
    } catch {
        /* ignore */
    }

    // Let layout settle after meta change; never jump scrollY to 0.
    await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                window.setTimeout(() => resolve(), 60);
            });
        });
    });

    return { stillZoomed: isVisualViewportZoomed() };
}

/** Undo tour-only viewport meta (re-enable pinch zoom). Safe if never locked. */
export function restoreViewportAfterTour(): void {
    if (!tourViewportLocked) return;
    try {
        const meta = document.querySelector('meta[name="viewport"]');
        if (meta instanceof HTMLMetaElement) {
            meta.setAttribute('content', savedViewportContent ?? DEFAULT_VIEWPORT_CONTENT);
        }
    } catch {
        /* ignore */
    }
    savedViewportContent = null;
    tourViewportLocked = false;
}
